/**
 * <marquee-band> — the scrolling band of phrases.
 *
 * The seam is the whole problem. A marquee that animates a fixed percentage only
 * loops cleanly when the track is exactly two identical halves, which means the
 * number of copies has to change with the phrase list and the screen width. So
 * instead: measure one pass, repeat it until the band is covered, and animate by
 * exactly that measured width. Any number of copies then loops seamlessly.
 *
 * Duration comes from that width and a px-per-second setting, so a long list and
 * a short one travel at the same speed rather than taking the same time.
 *
 * The copies are hidden from assistive technology — the phrases are in the DOM
 * several times, but they are one message.
 */
(() => {
  class MarqueeBand extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]');
      this.group = this.querySelector('[data-marquee-group]');
      this.toggle = this.querySelector('[data-marquee-toggle]');
      if (!this.track || !this.group) return;

      this.speed = Math.max(1, Number(this.dataset.speed) || 60);

      this.#measure();

      // A band that repeats forever is exactly what "reduce motion" is about, so
      // it starts still. The button is still there for anyone who wants it going.
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.#run(!this.reduceMotion);

      this.toggle?.addEventListener('click', () => this.#run(!this.running));

      // Re-measuring on resize matters less for the width of the text than for how
      // many copies are needed to cover a wider window.
      this.observer = new ResizeObserver(() => this.#measure());
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    #measure() {
      const groupWidth = this.group.getBoundingClientRect().width;
      // Nothing to loop: no phrases, or the fonts haven't arrived yet. Re-measured
      // by the resize observer once they do.
      if (groupWidth < 1) return;

      const needed = Math.ceil(this.getBoundingClientRect().width / groupWidth) + 1;

      // Rebuild only when the count actually changes; otherwise every resize tick
      // would restart the animation.
      if (this.copies !== needed) {
        this.copies = needed;
        const clones = [];
        for (let i = 1; i < needed; i += 1) {
          const clone = this.group.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-marquee-group');
          clones.push(clone);
        }
        this.track.replaceChildren(this.group, ...clones);
      }

      this.style.setProperty('--marquee-shift', `${groupWidth}px`);
      this.style.setProperty('--marquee-duration', `${groupWidth / this.speed}s`);
    }

    #run(shouldRun) {
      this.running = shouldRun;
      this.classList.toggle('is-running', shouldRun);
    }
  }

  if (!customElements.get('marquee-band')) {
    customElements.define('marquee-band', MarqueeBand);
  }
})();
